import React, { useEffect, useState } from "react";
import axios from "axios";
import { server } from "../index";
import {
  Box,
  Button,
  Container,
  HStack,
  Radio,
  RadioGroup,
} from "@chakra-ui/react";
import Loader from "./Loader";
import Error from "./Error";
import CoinCard from "./CoinCard";
import { motion } from "framer-motion";

const Coins = () => {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(1);
  const [currency, setCurrency] = useState("inr");

  const currencySymbol =
    currency === "inr" ? "₹" : currency === "eur" ? "€" : "$";

  const changePage = (newPage) => {
    setPage(newPage);
    setLoading(true); // Reset loading state
  };

  const totalPages = 100; // Total number of pages
  const pageNumbersToShow = 15; // Number of page numbers to show
  const ellipsis = "...";

  useEffect(() => {
    const fetchCoins = async () => {
      try {
        const { data } = await axios.get(
          `${server}/coins/markets?vs_currency=${currency}&page=${page}`
        );
        setCoins(data);
        setLoading(false);
      } catch (error) {
        setError(true);
        setLoading(false);
      }
    };
    fetchCoins();
  }, [currency, page]);

  if (error) return <Error message={"Error while fetching coins"} />;

  const renderPageNumbers = () => {
    const visiblePageNumbers = [];
    const startPage = Math.max(1, page - Math.floor(pageNumbersToShow / 2));
    const endPage = Math.min(totalPages, startPage + pageNumbersToShow - 1);

    for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
      visiblePageNumbers.push(
        <Button key={pageNum} onClick={() => changePage(pageNum)}>
          {pageNum}
        </Button>
      );
    }

    return (
      <>
        {startPage > 1 && (
          <>
            <Button onClick={() => changePage(1)}>Start</Button>
            <Button disabled>{ellipsis}</Button>
          </>
        )}

        {visiblePageNumbers}

        {endPage < totalPages && (
          <>
            <Button disabled>{ellipsis}</Button>
            <Button key={totalPages} onClick={() => changePage(totalPages)}>
              End
            </Button>
          </>
        )}
      </>
    );
  };

  return (
    <Container
      maxW={"container.xl"}
      bgColor={"blackAlpha.900"}
      p={{ base: "8", md: "16" }}
      pos="relative"
      overflow="hidden"
    >
      {/* Left decorative line */}
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: "20px" }}
        transition={{ duration: 0.5 }}
        pos="absolute"
        top="0"
        left="-20px"
        h="100%"
        bgColor="whiteAlpha.600"
      />

      {loading ? (
        <Loader />
      ) : (
        <>
          <RadioGroup value={currency} onChange={setCurrency} p={"8"}>
            <HStack spacing={"4"} my={"-8"}>
              <Radio value="inr">
                <Box color={"whiteAlpha.800"}>₹ INR</Box>
              </Radio>
              <Radio value="usd">
                <Box color={"whiteAlpha.800"}>$ USD</Box>
              </Radio>
              <Radio value="eur">
                <Box color={"whiteAlpha.800"}>€ EUR</Box>
              </Radio>
            </HStack>
          </RadioGroup>
          <HStack wrap={"wrap"} justifyContent={"space-evenly"}>
            {coins.map((i) => (
              <CoinCard
                id={i.id}
                key={i.id}
                name={i.name}
                img={i.image}
                symbol={i.symbol}
                price={i.current_price}
                currencySymbol={currencySymbol}
              />
            ))}
          </HStack>
          <HStack w={"full"} overflowX={"auto"} p={"8"}>
            <Button
              onClick={() => changePage(page - 1)}
              isDisabled={page === 1}
            >
              {"<"}
            </Button>
            {renderPageNumbers()}
            <Button
              onClick={() => changePage(page + 1)}
              isDisabled={page === totalPages}
            >
              {">"}
            </Button>
          </HStack>
        </>
      )}

      {/* Right decorative line */}
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: "20px" }}
        transition={{ duration: 0.5 }}
        pos="absolute"
        top="0"
        right="-20px"
        h="100%"
        bgColor="whiteAlpha.600"
      />
    </Container>
  );
};

export default Coins;
